import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { BillingService, ProductPricing } from '@galaxy/billing';
import {
  App,
  AppDataService,
  AppKey,
  AppPartnerService,
  AppPrice,
  AppPrices,
  AppPricingService,
  GetMultiAppRequest,
  GetMultiAppResponse,
  PartnerApiService,
} from '@galaxy/marketplace-apps';
import { AppEditionsMarketing, EditionsService, Release, Rule } from '@galaxy/marketplace-apps/v1';
import { FieldMask } from '@galaxy/partner';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { Addon, ENUM_TO_LMI_ARRAY } from '@vendasta/marketplace-packages';
import { Observable, combineLatest, of } from 'rxjs';
import { catchError, map, shareReplay, startWith, switchMap } from 'rxjs/operators';
import { EditionActionClickEvent } from '../product-details-v2/product-details-v2.component';
import { MarketingType, PersonaType } from '../product-details-v2/product-details-v2.interface';
import { MarketplaceAppData } from '../shared/marketplace-app-data';
import { Product } from '../shared/product';
import { HeaderActionType } from '../store-item';

@Component({
  selector: 'store-marketplace-product',
  templateUrl: './marketplace-product.component.html',
  styleUrls: ['./marketplace-product.component.scss'],
  standalone: false,
})
export class MarketplaceProductComponent implements OnInit, OnChanges {
  @Input() productId: string;
  @Input() partnerId: string;
  @Input() marketId: string;
  @Input() headerActionType: HeaderActionType;
  @Input() appPrice: AppPrice;
  @Input() currency: string;
  @Input() hideTags = false;

  @Output() headerActionClick = new EventEmitter<null>();
  @Output() addonClick = new EventEmitter<string>();
  @Output() editionActionClick = new EventEmitter<EditionActionClickEvent>();
  @Output() contactActionClick = new EventEmitter<null>();

  app$: Observable<App>;
  product$: Observable<Product>;
  addonPrices$: Observable<AppPrices[]>;
  appData$: Observable<MarketplaceAppData[]>;
  pricing$: Observable<{ [sku: string]: ProductPricing }>;
  editionsMarketing$: Observable<AppEditionsMarketing[]>;
  accessToAllRequiredAppsAndEditions$: Observable<{ hasAccess: boolean }>;

  isEditionActionUsed = false;

  marketingType = MarketingType;
  PersonaType = PersonaType;

  constructor(
    private partnerApiService: PartnerApiService,
    private appDataService: AppDataService,
    private billingService: BillingService,
    private editionsService: EditionsService,
    private snackbarService: SnackbarService,
    private appPartnerService: AppPartnerService,
    private appPricingService: AppPricingService,
  ) {}

  ngOnInit(): void {
    this.loadComponentData();
  }

  ngOnChanges(changes: SimpleChanges): void {
    const properties = ['productId', 'partnerId', 'marketId', 'headerActionType'];
    if (hasChanges(properties, changes)) {
      this.loadComponentData();
    }
  }

  loadComponentData(): void {
    this.isEditionActionUsed = this.editionActionClick.observers.length > 0;
    this.app$ = this.partnerApiService
      .getMultiApp(
        new GetMultiAppRequest({
          appKeys: [
            {
              appId: this.productId,
              editionId: null,
            },
          ],
          projectionFilter: null,
          partnerId: this.partnerId,
          marketId: this.marketId,
        }),
      )
      .pipe(
        catchError(() => {
          this.snackbarService.openErrorSnack('FRONTEND.STORE.ERROR_PRODUCT');
          return of(new GetMultiAppResponse({ apps: [] }));
        }),
        map((resp: GetMultiAppResponse) => {
          return resp?.apps[0];
        }),
        shareReplay(1),
      );

    const childApps$ = this.app$.pipe(
      switchMap((parentApp) => {
        const childIds = parentApp.childApps?.map((child) => child.appId);
        if (!childIds || childIds.length <= 0) return of(new GetMultiAppResponse({ apps: [] }));

        return this.partnerApiService.getMultiApp(
          new GetMultiAppRequest({
            appKeys: childIds.map((childId) => {
              return {
                appId: childId,
                editionId: null,
              };
            }),
            projectionFilter: null,
            partnerId: this.partnerId,
            marketId: this.marketId,
          }),
        );
      }),
      map((resp: GetMultiAppResponse) => {
        const validApps = resp?.apps.filter((app) => !!app) || [];
        if (!validApps || validApps.length <= 0) return [] as App[];

        return (
          validApps.sort((a, b) => {
            if (a.sharedMarketingInformation?.name < b.sharedMarketingInformation?.name) return -1;
            return 1;
          }) || new Array<App>()
        );
      }),
      shareReplay(1),
    );

    const addons$ = childApps$.pipe(
      switchMap((apps) => {
        const convertedAddons = apps.map((app) => {
          return this.convertAppToAddon(app);
        });

        return of(convertedAddons);
      }),
    );

    this.product$ = combineLatest([this.app$, addons$]).pipe(
      map(([app, addons]) => this.convertAppToProduct(app, addons)),
    );
    this.accessToAllRequiredAppsAndEditions$ = this.product$.pipe(
      switchMap((product) => {
        const rules = product?.restrictions?.appRequirement?.rules || [];
        const firstRule = rules.length > 0 ? rules[0] : new Rule();
        const requiredAppAppKeys = (firstRule.appIds || []).map((appId) => new AppKey({ appId }));
        const requiredEditionAppKeys = (firstRule.requiredEditions || []).map(
          (e) => new AppKey({ appId: e.appId, editionId: e.editionId ?? '' }),
        );
        const appKeys = requiredAppAppKeys.concat(requiredEditionAppKeys);
        return this.appPartnerService.getMulti(appKeys, this.partnerId, this.marketId, null, false).pipe(
          map((apps) => {
            return { hasAccess: appKeys.every((appKey) => apps.some((app) => appKey.appId === app?.key?.appId)) };
          }),
        );
      }),
      startWith({ hasAccess: true }),
    );

    this.addonPrices$ = this.product$.pipe(
      switchMap((product) => {
        const addonIds = product.addons.map((addon) => addon.addonId);
        if (addonIds.length > 0) {
          return this.appPricingService.getMultiPricing(
            addonIds,
            new FieldMask({ paths: ['retail'] }),
            this.partnerId,
            this.marketId,
          );
        }
        return of(new Array<AppPrices>());
      }),
      shareReplay(1),
    );

    this.appData$ = this.app$.pipe(
      switchMap((app) => {
        return this.appDataService.getMultiAppData('', '', [app.key.appId]);
      }),
      shareReplay(1),
    );

    this.pricing$ = this.app$.pipe(
      switchMap((app) => {
        const editions = app.editionInformation?.editions || [];
        if (editions.length > 0) {
          return this.billingService.getMultiRetailPricing(
            this.partnerId,
            null,
            editions.map((ed) => ed.billingId),
            this.marketId,
          );
        } else {
          return this.billingService.getMultiRetailPricing(
            this.partnerId,
            null,
            [app.externalIdentifiers.billingId],
            this.marketId,
          );
        }
      }),
      catchError((err) => {
        console.error(err);
        return of({});
      }),
    );

    this.editionsMarketing$ = this.app$.pipe(
      switchMap((app) => {
        if (typeof app.editionInformation.editions !== 'undefined' && app.editionInformation.editions.length > 0) {
          return this.editionsService.listEditionsMarketingForApp(app.key.appId, Release.RELEASE_PUBLISH);
        } else {
          return of([]);
        }
      }),
    );
  }

  convertAppToProduct(app: App, addons: Addon[]): Product {
    const product = new Product({
      partnerId: this.partnerId,
      productId: this.productId,
      editionId: '',
      editionName: '',
      usesEditions: app.editionInformation.usesEditions,
      name: app.sharedMarketingInformation.name,
      description: app.endUserMarketingInformation.description,
      tagline: app.sharedMarketingInformation.tagline,
      iconUrl: app.sharedMarketingInformation.iconUrl,
      headerImageUrl: app.sharedMarketingInformation.bannerImageUrl,
      entryUrl: app.ssoIntegration.entryUrl,
      screenshotUrls: app.endUserMarketingInformation.screenshotUrls,
      pdfUploadUrls: app.endUserMarketingInformation.fileUrls, //??
      lmiCategories: app.sharedMarketingInformation.lmiCategories
        ? app.sharedMarketingInformation.lmiCategories.map((lmi) => ENUM_TO_LMI_ARRAY[lmi.valueOf()].name)
        : [],
      keySellingPoints: app.endUserMarketingInformation.keySellingPoints,
      faqs: app.endUserMarketingInformation.faqs,
      needHelp: app.endUserMarketingInformation.needHelp,
      wholesalePrice: 0,
      setupFee: 0,
      usesCustomPricing: false,
      currency: '',
      billingFrequency: 'monthly',
      billingId: app.externalIdentifiers.billingId,
      isArchived: false,
      isSuspended: false,
      addons: addons,
      files: app.endUserMarketingInformation.fileUrls, //?
      restrictions: {
        country: app.activationInformation.countryRestrictions,
        appRequirement: {
          rules: [
            {
              appIds: app.activationInformation?.appRestrictions?.requiredAppIds ?? [],
              requiredEditions: app.activationInformation?.appRestrictions?.requiredEditionIds ?? [],
            },
          ],
        },
      },
      trialConfiguration: app.trialConfiguration,
      // current hack to beat the shouldContactSales class method of Pricing
      pricing: { prices: [{ price: '1111' }] },
    });
    product.videos = app.endUserMarketingInformation.videos;
    return product;
  }

  convertAppToAddon(app: App): Addon {
    return new Addon({
      appId: this.productId,
      addonId: app.key.appId,
      title: app.sharedMarketingInformation.name,
      icon: app.sharedMarketingInformation.iconUrl,
    });
  }

  handleHeaderButtonClick(): void {
    this.headerActionClick.emit();
  }

  handleAddonClick(addonId: string): void {
    this.addonClick.emit(addonId);
  }

  handleContactActionClick(): void {
    this.contactActionClick.emit();
  }
}

export function hasChanges(properties: string[], changes: SimpleChanges): boolean {
  return properties.some((p) => {
    const change = changes[p];
    return change?.isFirstChange() || change?.currentValue !== change?.previousValue;
  });
}
